import styled, { css } from 'styled-components';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { BorderRadius } from '@r1-frontend/ui-react/tokens/borderRadius';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { FONTS } from '@r1-frontend/ui-react/tokens/fonts';

import { IGradientSettings } from '~/src/features/movix-new/Card/index';

const TEXT_SETTINGS = css`
    ${FONTS.S};
    color: ${COLORS.TextWhite};

    @media (max-width: ${wideBreakpoints.sDesktop}) {
        ${FONTS.XS};
    }

    @media (max-width: ${wideBreakpoints.tablet}) {
        ${FONTS.XXS};
    }
`;

export const CardWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background: ${COLORS.BgMain}0D;
    border-radius: ${BorderRadius.OuterBlockRadius};
    padding: 16px;
    max-width: 480px;
`;

export const Title = styled.div`
    ${FONTS.H3};
    color: ${COLORS.TextWhite};
    white-space: nowrap;
    
    @media (max-width: ${wideBreakpoints.sDesktop}) {
        ${FONTS.H4};
    }

    @media (max-width: ${wideBreakpoints.tablet}) {
        white-space: wrap;
        padding-left: 0;
        padding-right: 0;
    }
`;

export const DescriptionList = styled.ul`
    display: flex;
    flex-direction: column;
    margin: 24px 0;
    list-style: disc;
    flex-grow: 1;
    padding-right: 16px;
    padding-left: 16px;

    li {
        ${TEXT_SETTINGS};
        margin-left: 4px;

        @media (max-width: ${wideBreakpoints.tablet}) {
            margin-left: 18px;
        }
    }

    @media (max-width: ${wideBreakpoints.tablet}) {
        padding-right: 0;
        padding-left: 0;
    }
`;

export const Description = styled.div`
    width: 100%;
    margin-top: 16px;
    flex-grow: 1;
    ${TEXT_SETTINGS};
    margin-bottom: 8px;

    @media (max-width: ${wideBreakpoints.tablet}) {
        padding-left: 0;
        padding-right: 0;
    }
`;

export const UnderButtonText = styled.div`
    ${FONTS.S};
    color: ${COLORS.TextHint};
    margin-top: 21px;
    width: 100%;
    justify-content: center;
    padding-left: 32px;
    padding-right: 32px;

    @media (max-width: ${wideBreakpoints.sDesktop}) {
        ${FONTS.XXS};
        margin-top: 9px;
    }

    @media (max-width: ${wideBreakpoints.tablet}) {
        margin-top: 9px;
        padding-left: 0;
        padding-right: 0;
    }

    @media (max-width: ${wideBreakpoints.mobile}) {
        margin-top: 9px;
    }
`;

export const ButtonBlock = styled.div`
    ${FONTS.S}
    min-height: 60px;
    width: auto;

    @media (max-width: ${wideBreakpoints.sDesktop}) {
        ${FONTS.XS};
        min-height: 45px;
        padding: 15px 0;
    }

    @media (max-width: ${wideBreakpoints.tablet}) {
        ${FONTS.XXS};
        min-height: 34px;
        padding: 10px 0;
        margin: 0 auto;
        width: 100%;
    }

    @media (max-width: ${wideBreakpoints.mobile}) {
        ${FONTS.XS};
        min-height: 40px;
        padding: 14.5px 0;
        width: -webkit-fill-available;
    }
`;

export const Image = styled.div<{
    path: string,
    isBigImage?: boolean,
    gradientSettings?: IGradientSettings,
    notNeedBgStyleAdditional?: boolean,
}>`
    width: 100%;
    height: 221px;
    background: url(${({ path }) => path}) center no-repeat;
    margin-bottom: 15px;
    background-size: ${({ isBigImage }) => isBigImage ? 'cover !important' : 'contain'};
    position: relative;
    border-radius: ${BorderRadius.OuterBlockRadius};

    ${({ gradientSettings }) => gradientSettings && css`
      :before {
        content: '';
        position: absolute;
        ${gradientSettings.bottom && css`bottom: ${gradientSettings.bottom};`}
        ${gradientSettings.left && css`left: ${gradientSettings.left};`}
        ${gradientSettings.right && css`right: ${gradientSettings.right};`}
        ${gradientSettings.top && css`top: ${gradientSettings.top};`}
        ${gradientSettings.borderRadius && css`border-radius: ${gradientSettings.borderRadius};`}
        background: ${gradientSettings.gradient};
        width: ${gradientSettings.width ? `${gradientSettings.width}px` : '100%'};
        height: ${gradientSettings.height}px;
      }
    `};

    @media (max-width: ${wideBreakpoints.sDesktop}) {
        height: 167px;
        background-size: 100% 100%;
    }

    @media (max-width: ${wideBreakpoints.tablet}) {
        height: 124px;

        ${({ notNeedBgStyleAdditional, isBigImage }) => !notNeedBgStyleAdditional && css`
            background-size: ${isBigImage ? 'cover' : '100% 124px'};
        `}
    }

    @media (max-width: ${wideBreakpoints.mobile}) {
        height: 162px;

        ${({ notNeedBgStyleAdditional, isBigImage }) => !notNeedBgStyleAdditional && css`
        background-size: ${isBigImage ? 'cover' : '100% 162px'};
        `}

    }
`;

export const Price = styled.div`
    ${FONTS.H1};
    color: ${COLORS.TextWhite};
    white-space: nowrap;

    @media (max-width: ${wideBreakpoints.sDesktop}) {
        ${FONTS.H3};
    }

    @media (max-width: ${wideBreakpoints.tablet}) {
        ${FONTS.H4};
    }

    @media (max-width: ${wideBreakpoints.mobile}) {
        ${FONTS.H3};
    }
`;

export const Discount = styled.div`
    ${FONTS.XS};
    max-width: 128px;
    width: 100%;
    height: fit-content;

    color: ${COLORS.TextHint};

    @media (max-width: ${wideBreakpoints.tablet}) {
        font-size: 8px;
        line-height: 2;
        max-width: 71px;
    }

    @media (max-width: ${wideBreakpoints.mobile}) {
        font-size: 12px;
        line-height: 1.33;
        max-width: 105px;
    }
`;

export const PriceBlock = styled.div`
    display: flex;
    flex-wrap: nowrap;
    width: fit-content;
    align-items: center;
    column-gap: 29px;
    margin-bottom: 21px;

    @media (max-width: ${wideBreakpoints.sDesktop}) {
        column-gap: 21px;
        margin-bottom: 15px;
    }

    @media (max-width: ${wideBreakpoints.tablet}) {
        column-gap: 15px;
        margin-bottom: 13px;
    }

    @media (max-width: ${wideBreakpoints.mobile}) {
        justify-content: space-between;
        width: 100%;
        margin-bottom: 10px;
    }
`;

export const FullPrice = styled.div`
    ${FONTS.H1};
    color: ${COLORS.TextWhite};

    @media (max-width: ${wideBreakpoints.sDesktop}) {
        ${FONTS.H3};
    }

    @media (max-width: ${wideBreakpoints.tablet}) {
        ${FONTS.H4};
    }

    @media (max-width: ${wideBreakpoints.mobile}) {
        ${FONTS.H3};
    }
`;

export const FullPriceBlock = styled.div`
    display: flex;
    flex-direction: column;
    width: fit-content;
    margin-bottom: 16px;
    margin-top: 28px;

    @media (max-width: ${wideBreakpoints.sDesktop}) {
        margin-bottom: 11px;
        margin-top: 23px;
    }

    @media (max-width: ${wideBreakpoints.tablet}) {
        margin-bottom: 8px;
        margin-top: 8px;
        padding-left: 0;
    }
`;

export const Leasing = styled.div<{ isActive: boolean }>`
    display: flex;
    align-items: center;
    position: relative;
    color: ${COLORS.TextWhite};
    ${FONTS.M};
    line-height: 100%;
    opacity: 0.5;
    width: fit-content;
    padding-right: 23px;
    cursor: pointer;

    :after {
        content: '';
        position: absolute;
        top: 5px;
        right: 0;
        width: 2px;
        height: 10px;
        border-radius: 10px;
        background: ${COLORS.BgSecondary};
        transform: rotate(${({ isActive }) => isActive ? -45 : 45}deg);
    }

    :before {
        content: '';
        position: absolute;
        top: 5px;
        right: 6px;
        width: 2px;
        height: 10px;
        border-radius: 10px;
        background: ${COLORS.BgSecondary};
        transform: rotate(${({ isActive }) => isActive ? 45 : -45}deg);
    }

    ${FONTS.S};
    line-height: 1;

    :before {
        top: 6px;
        height: 8px;
    }

    :after {
        top: 6px;
        right: 1px;
        height: 8px;
    }

    @media (max-width: ${wideBreakpoints.sDesktop}) {
        ${FONTS.XXS}
        line-height: 1;
        padding-right: 14px;

        :before {
            top: 3px;
            right: 4px;
            height: 7px;
        }

        :after {
            top: 3px;
            height: 7px;
        }
    }

    @media (max-width: ${wideBreakpoints.tablet}) {
        font-size: 9px;
        line-height: 1;
        padding-right: 10px;

        :before {
            top: 3px;
            right: 4px;
            height: 5px;
        }

        :after {
            top: 3px;
            height: 5px;
            right: 1px;
        }
    }
`;
